import React, { Component, useEffect, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import './ui.css';

/**
 * Loading: grey shapes where the content will be. Nothing shows for the
 * first moment, so a fast load doesn't flash. Screen readers hear "Loading…".
 */
export function Skeleton({ rows = 4, label = 'Loading…', delay = 300 }) {
  const [shown, setShown] = useState(delay === 0);
  useEffect(() => {
    if (delay === 0) return undefined;
    const t = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div className="ui-skeleton" role="status" aria-label={label}>
      <span className="ui-sr-only">{label}</span>
      {shown && Array.from({ length: rows }, (_, i) => (
        <div key={i} className="ui-skeleton__card" aria-hidden="true">
          <div className="ui-skeleton__line" style={{ width: '55%', height: '0.85rem' }} />
          <div className="ui-skeleton__line" style={{ width: '80%' }} />
        </div>
      ))}
    </div>
  );
}

/**
 * Nothing to show yet: what's missing, why it matters, and (when the viewer
 * can do something about it) the button to fix it.
 * icon: a Heroicons 24/outline component. action: a button or link element.
 */
export function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div className="ui-state">
      {Icon && <Icon aria-hidden="true" />}
      <h2 className="ui-state__title">{title}</h2>
      {text && <p className="ui-state__text">{text}</p>}
      {action}
    </div>
  );
}

/**
 * A page that couldn't load: "Couldn't load invoices", what to do, and Try
 * again. The page's back link stays above it, so there's always a way out.
 */
export function ErrorState({ title, text = 'Check your connection, then try again.', onRetry }) {
  return (
    <div className="ui-state ui-state--error" role="alert">
      <ExclamationTriangleIcon aria-hidden="true" />
      <h2 className="ui-state__title">{title}</h2>
      {text && <p className="ui-state__text">{text}</p>}
      {onRetry && <button type="button" className="bk-btn bk-btn--primary" onClick={onRetry}>Try again</button>}
    </div>
  );
}

/** Part of a page that couldn't load: just that card shows it */
export function InlineError({ children, onRetry }) {
  return (
    <div className="ui-inline-error" role="alert">
      <span>{children}</span>
      {onRetry && <button type="button" className="bk-btn bk-btn--sm" onClick={onRetry}>Try again</button>}
    </div>
  );
}

/**
 * The catch-all: one broken page never blanks the whole app. Give it
 * key={location.pathname} so moving to another page starts it afresh.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Page crashed', error, info?.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="ui-state ui-state--error" role="alert" style={{ paddingTop: '3rem' }}>
        <ExclamationTriangleIcon aria-hidden="true" />
        <h2 className="ui-state__title">Something went wrong on this page</h2>
        <p className="ui-state__text">Reloading usually fixes it.</p>
        <div className="bk-actions">
          <button type="button" className="bk-btn bk-btn--primary" onClick={() => window.location.reload()}>Reload</button>
          <a className="bk-btn" href="/">Go home</a>
        </div>
      </div>
    );
  }
}
